/**
 * Contracts for `pending-business-pdf-reports` — public share-links
 * over suite reports, optional PDF rendition.
 *
 * Mirrors `mock-server` share-link shapes so the auth'd UI for both
 * looks identical. Tokens are opaque (server-generated), expiry is
 * mandatory, revocation is permanent.
 */

import { z } from 'zod';

export const ReportShareLinkFormatSchema = z.enum(['html', 'pdf']);
export type ReportShareLinkFormat = z.infer<typeof ReportShareLinkFormatSchema>;

export const ReportShareLinkSchema = z.object({
  id: z.string().uuid(),
  testReportId: z.string().uuid(),
  orgId: z.string().uuid(),
  token: z.string().min(20),
  rateLimitPerMin: z.number().int().min(1).max(60_000),
  expiresAt: z.string().datetime(),
  revokedAt: z.string().datetime().nullable(),
  viewCount: z.number().int().nonnegative(),
  lastViewedAt: z.string().datetime().nullable(),
  createdByUserId: z.string().uuid(),
  createdAt: z.string().datetime(),
  /** Convenience field — the full `https://…/reports/share/<token>` URL.
   *  Server-populated; not stored in DB. */
  publicUrl: z.string().url(),
});
export type ReportShareLink = z.infer<typeof ReportShareLinkSchema>;

export const CreateReportShareLinkRequestSchema = z.object({
  /** Optional override; otherwise the operator default applies. */
  expiresAt: z.string().datetime().optional(),
  /** Optional override; otherwise the operator default applies. */
  rateLimitPerMin: z.number().int().min(1).max(60_000).optional(),
});
export type CreateReportShareLinkRequest = z.infer<typeof CreateReportShareLinkRequestSchema>;

export const ListReportShareLinksResponseSchema = z.object({
  items: z.array(ReportShareLinkSchema),
});
export type ListReportShareLinksResponse = z.infer<typeof ListReportShareLinksResponseSchema>;

export const ReportShareLinkHitSchema = z.object({
  id: z.string().uuid(),
  shareLinkId: z.string().uuid(),
  sourceIp: z.string().nullable(),
  userAgent: z.string().nullable(),
  statusCode: z.number().int(),
  durationMs: z.number().int().nonnegative(),
  format: ReportShareLinkFormatSchema,
  blockedReason: z.string().nullable(),
  createdAt: z.string().datetime(),
});
export type ReportShareLinkHit = z.infer<typeof ReportShareLinkHitSchema>;

export const ListReportShareLinkHitsResponseSchema = z.object({
  items: z.array(ReportShareLinkHitSchema),
});
export type ListReportShareLinkHitsResponse = z.infer<typeof ListReportShareLinkHitsResponseSchema>;
